/**
 * ModeBadge, surfaces D-088 connector mode honestly to admins.
 *
 *   "Hosted by Olytic", connector_mode='custom', Olytic owns the microservice
 *                        and the full SLA. Tier 4 governance available.
 *
 *   "{Vendor} MCP"    , connector_mode='proxy', Olytic gateway forwards
 *                        to the vendor's upstream MCP server. Tiers 0-3
 *                        only; SLA depends on vendor uptime.
 *
 *   "Hosted by Composio", connector_mode='broker', Olytic routes calls
 *                        through Composio's managed connector platform.
 *
 * The label appears in the catalog detail page, the admin install modal,
 * and the AdminConnectors instance list so admins know what they're getting
 * before they install.
 *
 * Rendered with Foundation's Badge (soft, pill): info tone for custom,
 * neutral for proxy and broker. Foundation's Badge is uppercase by design (PLAT-277).
 */

import { Badge } from '@olytic/foundation';
import type { ConnectorMode } from '../lib/api.ts';

interface ModeBadgeProps {
  mode: ConnectorMode;
  /** Vendor display name, used for the "{Vendor} MCP" rendering of proxy mode. */
  vendorName: string;
}

export function ModeBadge({ mode, vendorName }: ModeBadgeProps) {
  switch (mode) {
    case 'custom':
      return (
        <Badge
          tone="info"
          variant="soft"
          shape="pill"
          title="Olytic-owned Cloud Run microservice. Full SLA + Tier 4 governance."
        >
          Hosted by Olytic
        </Badge>
      );
    case 'proxy':
      return (
        <Badge
          tone="neutral"
          variant="soft"
          shape="pill"
          title="Olytic gateway forwards to the vendor's upstream MCP server. SLA depends on vendor uptime."
        >
          {vendorName} MCP
        </Badge>
      );
    case 'broker':
      return (
        <Badge
          tone="neutral"
          variant="soft"
          shape="pill"
          title="Composio brokers the connection; Olytic routes calls through its gateway."
        >
          Hosted by Composio
        </Badge>
      );
  }
}

/**
 * SlaDisclosure, small italic footnote rendered under connector tiles or
 * detail headers when a template supplies a service-level disclosure.
 */
export function SlaDisclosure({ text }: { text: string | null }) {
  if (text === null) return null;
  return (
    <p
      style={{
        margin: '6px 0 0',
        fontSize: '11px',
        color: 'var(--fg-muted)',
        fontStyle: 'italic',
      }}
    >
      {text}
    </p>
  );
}
